// ---------- l'avis sur le Play Store ----------
// Repris de Codigrid (src/review.ts là-bas), dont les règles ont été pesées une à une. On ne les
// réinvente pas ici, on les adapte : le jeu compte des PARTIES, pas des feuilles.
//
// LA CARTE N'EST PAS À NOUS. `requestReview()` demande seulement à Play d'afficher SA carte, et
// rien ne revient : l'API ne dit ni si la carte s'est affichée, ni si le joueur a noté. Play
// applique son propre quota et peut ne rien montrer, en résolvant la promesse comme si tout
// s'était bien passé. D'où la règle qui compte : on compte les TENTATIVES, jamais les succès —
// sinon on redemanderait sans fin à celui à qui Play n'a jamais rien montré.
//
// Le QUAND est donc le seul levier. Il est en entier dans canAskForReview() :
//  - assez joué (six parties : la quête a passé ses parties faciles, le joueur sait ce que le
//    jeu devient), depuis assez longtemps (trois jours : une habitude, pas une curiosité) ;
//  - REVENU : la dernière partie finie date d'une session passée. Jamais à la sortie d'une
//    partie, où la fin, les étoiles, le partage et l'invitation se succèdent déjà ;
//  - pas dans la semaine d'une mise à jour, où se découvrent les régressions ;
//  - trois fois au plus dans la vie d'une installation, un trimestre entre deux.

import { InAppReview } from '@capacitor-community/in-app-review';

import { isNative } from './platform';
import { doneTotal } from './store';

declare const __APP_VERSION__: string | undefined;
const VERSION = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '';

const KEY = 'verbigrid:review:v1';
const DAY = 86_400_000;
const MIN_PARTS = 6;
const MIN_AGE_MS = 3 * DAY;
const BETWEEN_MS = 120 * DAY;
const MAX_ASKS = 3;
const AFTER_UPDATE_MS = 7 * DAY;

interface ReviewState {
  /** Première ouverture connue de l'application. */
  first: number;
  /** Demandes TENTÉES (voir l'en-tête), et la dernière. */
  asks: number;
  last: number;
  /** Dernière partie finie : comparée à SESSION_START, elle dit si le joueur est revenu. */
  finishedAt: number;
  /** La version du dernier lancement, et quand la courante est apparue (0 : jamais de mise à
   *  jour vue — une installation neuve n'arme pas le silence de sept jours). */
  version: string;
  versionAt: number;
}

/** Figé à l'import : une partie finie AVANT appartient à une session passée. */
const SESSION_START = Date.now();

const blank = (): ReviewState => ({
  first: Date.now(),
  asks: 0,
  last: 0,
  finishedAt: 0,
  version: '',
  versionAt: 0,
});

function read(): ReviewState {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<ReviewState> | null;
    if (!raw || typeof raw !== 'object') return blank();
    const num = (v: unknown, d: number): number => (typeof v === 'number' ? v : d);
    return {
      first: num(raw.first, Date.now()),
      asks: num(raw.asks, 0),
      last: num(raw.last, 0),
      finishedAt: num(raw.finishedAt, 0),
      version: typeof raw.version === 'string' ? raw.version : '',
      versionAt: num(raw.versionAt, 0),
    };
  } catch {
    return blank();
  }
}

function write(state: ReviewState): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* le jeu continue, sans mémoire des demandes */
  }
}

/** Au lancement : pose la date de première ouverture et repère les mises à jour, bien avant la
 *  première question. */
export function initReview(): void {
  const state = read();
  if (state.version !== VERSION) {
    state.versionAt = state.version === '' ? 0 : Date.now();
    state.version = VERSION;
  }
  write(state);
}

/** Une partie vient d'être finie. Le seul écrivain de `finishedAt`. */
export function markPartFinished(): void {
  const state = read();
  state.finishedAt = Date.now();
  write(state);
}

export function canAskForReview(): boolean {
  if (!isNative()) return false; // la carte de Play n'existe que dans l'APK
  const state = read();
  const now = Date.now();
  if (state.asks >= MAX_ASKS) return false;
  if (state.last && now - state.last < BETWEEN_MS) return false;
  if (state.versionAt && now - state.versionAt < AFTER_UPDATE_MS) return false;
  if (now - state.first < MIN_AGE_MS) return false;
  // Compté dans la sauvegarde, pas par un compteur à part qui ne pourrait qu'en diverger.
  if (doneTotal() < MIN_PARTS) return false;
  return state.finishedAt > 0 && state.finishedAt < SESSION_START;
}

/** Demande la carte si le moment s'y prête. Appelée par main.ts une fois le chemin peint : une
 *  demande d'avis posée sur « Impossible de joindre le serveur » serait un avis sur une panne. */
export async function askForReview(): Promise<void> {
  if (!canAskForReview()) return;
  // Notée AVANT l'appel : si `requestReview()` lève, on ne saura jamais si la carte s'était
  // montrée. Une demande perdue vaut mieux qu'une demande répétée.
  const state = read();
  state.asks += 1;
  state.last = Date.now();
  write(state);
  try {
    await InAppReview.requestReview();
  } catch {
    /* pas de Play Services, version installée hors du Store : le jeu continue */
  }
}

const PACKAGE = 'fr.tidou44.verbigrid';

/** « Noter Verbigrid », depuis les Réglages. PAS `requestReview()` : Play peut ne rien afficher,
 *  et un bouton qui ne fait rien une fois sur deux est pire que pas de bouton. Le joueur a
 *  demandé, il doit arriver sur la fiche. */
export function openStoreListing(): void {
  if (isNative()) return void window.open(`market://details?id=${PACKAGE}`, '_system');
  window.open(`https://play.google.com/store/apps/details?id=${PACKAGE}`, '_blank', 'noopener');
}
